<template>
  <div class="home">
    <page-content>
      <div class="row">
        <div class="offset-md-1 col-md-10">
          <ntm-block>
            <div class="mb-4">
              <input
                v-model="search"
                @input="onSearch"
                type="text"
                class="form-control"
                :placeholder="$t('voting_form.searchPlayers')"
              />
            </div>

            <div class="position-relative" style="min-height: 200px">
              <le-loading v-if="isLoading"></le-loading>
              <div v-else>

                <!-- Search mode -->
                <template v-if="isSearchMode">
                  <div v-if="players.length" class="row">
                    <div
                      v-for="player in players"
                      :key="player.uuid"
                      class="col-12 col-md-6 col-lg-4 mb-3"
                    >
                      <player-card :player="player" @click="openCropModal(player)">
                        <div v-if="player.team" class="text-muted small text-truncate">{{ player.team }}</div>
                      </player-card>
                    </div>
                  </div>
                  <div v-else class="text-center py-4">
                    <h4>{{ $t('noData') }}</h4>
                  </div>
                </template>

                <!-- Default mode: players grouped by game -->
                <template v-else>
                  <div v-if="games.length">
                    <h4 class="mb-4">{{ $t('nearbyGamePlayers') }}</h4>
                    <div v-for="game in games" :key="game.uuid" class="block block-rounded mb-4">
                      <div class="block-header block-header-default" style="cursor: pointer;" @click="toggleGame(game.uuid)">
                        <h3 class="block-title">
                          {{ game.homeTeam.name }} <span class="text-muted font-w400">vs</span> {{ game.awayTeam.name }}
                          <span
                            class="badge ml-2"
                            style="font-size: 10px; vertical-align: middle;"
                            :class="{
                              'badge-success': ['FINISHED', 'ARCHIVED'].includes(game.status),
                              'badge-danger': game.status === 'LIVE',
                              'badge-info': game.status === 'SCHEDULED'
                            }"
                          >{{ $tc(`game.status.${game.status}`, 1) }}</span>
                        </h3>
                        <div class="block-options text-muted small">
                          <span v-if="game.venue" class="mr-2">{{ game.venue.name ?? game.venue }},</span>
                          <span v-if="game.league" class="mr-2">{{ game.league }},</span>
                          <span class="mr-2">{{ $gameTime(game.time, 'dateLong') }}</span>
                          <i :class="collapsed[game.uuid] ? 'fa-chevron-down' : 'fa-chevron-up'" class="fa"></i>
                        </div>
                      </div>
                      <div v-show="!collapsed[game.uuid]" class="block-content pb-3">
                        <div class="row">
                          <div v-for="team in [game.homeTeam, game.awayTeam]" :key="team.uuid" class="col-12 col-md-6 mb-2">
                            <h6 class="font-w700 text-muted text-uppercase mb-3" style="font-size: 11px; letter-spacing: 0.05em;">{{ team.name }}</h6>
                            <player-card
                              v-for="player in team.players"
                              :key="player.uuid"
                              :player="player"
                              class="mb-2"
                              @click="openCropModal(player)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-4 text-muted">
                    <p>{{ $t('noData') }}</p>
                  </div>
                </template>

              </div>
            </div>
          </ntm-block>
        </div>
      </div>
    </page-content>

    <ntm-modal @closed="selectedPlayer = null" size="lg" ref="cropModal">
      <ntm-crop
        v-if="selectedPlayer"
        :key="selectedPlayer.uuid"
        @change="onProfileUpdated"
        entity-type="player"
        :entity-uuid="selectedPlayer.uuid"
        collection="profile"
        :circle="true"
      ></ntm-crop>
    </ntm-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import GamePhotosService from '@/services/gamePhotos.service'
import LeLoading from '@/components/le/LeLoading.vue'
import PlayerCard from '@/components/le/PlayerCard.vue'

export default {
  components: { LeLoading, PlayerCard },
  data () {
    return {
      search: '',
      players: [],
      games: [],
      collapsed: {},
      isLoading: false,
      selectedPlayer: null
    }
  },
  computed: {
    isSearchMode () {
      return this.search.length >= 2
    }
  },
  beforeRouteEnter (to, from, next) {
    GamePhotosService.playerPhotosGames().then((res) => {
      next(vm => {
        vm.games = res.data
        const collapsed = {}
        res.data.forEach(g => { collapsed[g.uuid] = true })
        vm.collapsed = collapsed
      })
    })
  },
  methods: {
    onSearch () {
      if (!this.isSearchMode) {
        this.players = []
        return
      }
      this.doSearch(this.search)
    },
    doSearch: _.debounce(function (search) {
      this.isLoading = true
      GamePhotosService.searchPlayerPhotos(search).then((res) => {
        this.players = res.data
      }).finally(() => {
        this.isLoading = false
      })
    }, 300),
    toggleGame (uuid) {
      this.$set(this.collapsed, uuid, !this.collapsed[uuid])
    },
    openCropModal (player) {
      this.selectedPlayer = player
      this.$refs.cropModal.open()
    },
    onProfileUpdated (imageUrl) {
      if (this.isSearchMode) {
        const p = this.players.find(p => p.uuid === this.selectedPlayer.uuid)
        if (p) p.profile = imageUrl
      } else {
        this.games.forEach(g => {
          [g.homeTeam.players, g.awayTeam.players].forEach(list => {
            const p = list.find(p => p.uuid === this.selectedPlayer.uuid)
            if (p) p.profile = imageUrl
          })
        })
      }
      this.$refs.cropModal.close()
    }
  }
}
</script>
